import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import FloatingPartnerBanner from '../partners/FloatingPartnerBanner';
import WebsiteOfferBanner from '../partners/WebsiteOfferBanner';
import { COLORS, RADIUS, SHADOWS } from '../../styles/theme';

const styles = {
  header: {
    background: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${COLORS.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: SHADOWS.sm,
  },
  inner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 24px',
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    fontWeight: 800,
    fontSize: 20,
    color: COLORS.ink,
    flexShrink: 0,
    letterSpacing: '-0.01em',
  },
  // A single solid color instead of a 3-stop gradient -- reads as more
  // deliberate/premium than a rainbow-y gradient on a 34px icon, and one
  // less gradient in a page that otherwise leans on them heavily.
  logoIcon: {
    width: 34,
    height: 34,
    background: COLORS.primary,
    borderRadius: RADIUS.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 3px 10px rgba(37,99,235,0.28), inset 0 1px 0 rgba(255,255,255,0.16)',
  },
  nav: { display: 'flex', alignItems: 'center', gap: 4 },
  navLink: {
    padding: '8px 14px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: '#475569',
    transition: 'all 0.15s',
  },
  navLinkActive: {
    padding: '8px 14px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.primaryHover,
    background: COLORS.primaryMuted,
    transition: 'all 0.15s',
  },
  partnerLink: {
    padding: '8px 14px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.primary,
    transition: 'all 0.15s',
  },
  // "Get Estimator" is the single most important nav link (it's the whole
  // product, not just a page) -- a filled pill instead of plain text so it
  // reads as its own CTA next to the other links, with pulse-cta (App.css)
  // adding the subtle attention ring.
  estimatorLink: {
    padding: '8px 16px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.primaryHover,
    background: '#dbeafe',
    border: `1px solid ${COLORS.primary}55`,
    transition: 'all 0.15s',
  },
  cta: {
    background: COLORS.primary,
    color: 'white',
    padding: '9px 18px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    marginLeft: 8,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    boxShadow: SHADOWS.primary,
  },
  ctaMobile: {
    background: COLORS.primary,
    color: 'white',
    padding: '8px 14px',
    borderRadius: RADIUS.sm,
    textDecoration: 'none',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    transition: 'all 0.15s',
    boxShadow: '0 2px 8px rgba(37,99,235,0.22)',
  },
};

const navItems = [
  { label: 'Cost Calculator', href: '/cleaning-cost-calculator' },
  { label: 'Services', href: '/#services' },
  { label: 'Blog', href: '/blog' },
];

function isNavItemActive(href, pathname, hash) {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  if (href === '/') return path === '/' && hash !== '#services';
  if (href === '/#services') return path === '/' && hash === '#services';
  if (href === '/blog') return path === '/blog' || path.startsWith('/blog/');
  return path === href;
}

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  // matchMedia, not a raw 'resize' listener -- mobile Chrome's address-bar
  // collapse on scroll fires 'resize' (the layout viewport's height
  // changes) and can momentarily misreport width too, flipping this flag
  // mid-scroll on a long page. matchMedia's 'change' event only fires on
  // an actual breakpoint crossing.
  const mobileQuery = () => window.matchMedia('(max-width: 768px)');
  const [isMobile, setIsMobile] = useState(() => mobileQuery().matches);
  const [location, setLocation] = useState(() => ({
    pathname: window.location.pathname,
    hash: window.location.hash,
  }));

  useEffect(() => {
    const mq = mobileQuery();
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onLocationChange = () => setLocation({ pathname: window.location.pathname, hash: window.location.hash });
    window.addEventListener('hashchange', onLocationChange);
    window.addEventListener('popstate', onLocationChange);
    return () => {
      window.removeEventListener('hashchange', onLocationChange);
      window.removeEventListener('popstate', onLocationChange);
    };
  }, []);

  return (
    <header id="site-header" style={styles.header}>
      <FloatingPartnerBanner />
      <WebsiteOfferBanner />
      <div style={styles.inner}>
        <a href="/" style={{ ...styles.logo, fontSize: isMobile ? 16 : 20 }} aria-label="Clean Estimator — Free Cleaning Cost Estimator">
          <span style={styles.logoIcon} aria-hidden="true"><Sparkles size={17} strokeWidth={2.25} /></span>
          {!isMobile && 'Clean Estimator'}
        </a>

        {isMobile ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <a
              href="/estimator"
              className="pulse-cta"
              style={styles.ctaMobile}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(30,64,175,0.32)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(30,64,175,0.22)'; }}
            >
              Get Estimator
            </a>
            <button
              onClick={() => setMenuOpen(m => !m)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 4px', fontSize: 22, color: '#0f172a', lineHeight: 1 }}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        ) : (
          <nav style={styles.nav}>
            {navItems.map(n => {
              const active = isNavItemActive(n.href, location.pathname, location.hash);
              return (
                <a
                  key={n.href}
                  href={n.href}
                  aria-current={active ? 'page' : undefined}
                  style={active ? styles.navLinkActive : styles.navLink}
                  onMouseEnter={e => { if (!active) { e.target.style.color = '#0f172a'; e.target.style.background = '#f1f5f9'; } }}
                  onMouseLeave={e => { if (!active) { e.target.style.color = '#475569'; e.target.style.background = 'transparent'; } }}
                >
                  {n.label}
                </a>
              );
            })}
            <a
              href="/partner-with-us"
              style={styles.partnerLink}
              onMouseEnter={e => { e.target.style.background = '#eff6ff'; }}
              onMouseLeave={e => { e.target.style.background = 'transparent'; }}
            >
              Become a Partner
            </a>
            <a
              href="/estimator"
              className="pulse-cta"
              style={styles.estimatorLink}
              onMouseEnter={e => { e.target.style.background = '#bfdbfe'; }}
              onMouseLeave={e => { e.target.style.background = '#dbeafe'; }}
            >
              Get Estimator
            </a>
            <a
              href="/company"
              style={styles.cta}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(30,64,175,0.34)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(30,64,175,0.24)'; e.currentTarget.style.transform = 'none'; }}
            >
              Company Login →
            </a>
          </nav>
        )}
      </div>

      {isMobile && menuOpen && (
        <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 4, background: 'white' }}>
          {navItems.map(n => {
            const active = isNavItemActive(n.href, location.pathname, location.hash);
            return (
              <a
                key={n.href}
                href={n.href}
                aria-current={active ? 'page' : undefined}
                style={{ ...(active ? styles.navLinkActive : styles.navLink), display: 'block', padding: '10px 12px' }}
              >
                {n.label}
              </a>
            );
          })}
          <a href="/partner-with-us" style={{ ...styles.navLink, display: 'block', padding: '10px 12px', color: COLORS.primary, fontWeight: 700 }}>
            Become a Partner
          </a>
          <a href="/company" style={{ ...styles.cta, display: 'block', textAlign: 'center', marginLeft: 0, marginTop: 8 }}>
            Company Login →
          </a>
        </div>
      )}
    </header>
  );
}
